import { mergePlaybookJobs, setPlaybookStreamActive } from './playbook-queue';
import {resolveCanvasProduct} from './canvas-product-choice';
import { applyCloneProgress, createCloneProgress, useCloneActivity } from './canvas-clone-progress';
import { useState, useRef, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useCanvasStore } from './canvas-store';
import { useActiveProjectImages } from './store-selectors';

interface CreateVariationsPanelProps {
  selectedImageId: string;
  onClose: () => void;
}

const CREATIVITY_LABEL = (val: number) => {
  if (val <= 30) return 'Copy Only';
  if (val <= 70) return 'Balanced';
  return 'Full Creative';
};

export function CreateVariationsPanel({ selectedImageId, onClose }: CreateVariationsPanelProps) {
  const [productChoice,setProductChoice]=useState<{candidates:{id:number;name:string}[];qty:number;mode:'clone'|'variation'}|null>(null);
  const [productError,setProductError]=useState('');
  const checkingProduct=useRef(false);
  const scope=useRef('');
  const requestEpoch=useRef(0);
  const [qty, setQty] = useState(2);
  const [creativity, setCreativity] = useState(50);
  const [isGenerating, setIsGenerating] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  // The panel acts as the UI for two different pipelines — strict Clone and
  // creativity-tunable Variation. The mode is picked at the time generation
  // starts (either via the auto-fire effect's `pending.mode` or as a default
  // 'variation' when the user clicks Generate manually). We mirror it here so
  // labels/captions across the panel reflect what's actually happening.
  const [activeMode, setActiveMode] = useState<'clone' | 'variation'>('variation');
  const isCloneMode = activeMode === 'clone';

  const { addImageNextTo, addImageToCanvas, addCanvasPlaceholder, resolveCanvasPlaceholder, failCanvasPlaceholder, saveConversationHistory } = useCanvasStore();
  const activeBrandId = useCanvasStore((s) => s.activeBrandId);
  const activeProjectId = useCanvasStore((s) => s.activeProjectId);
  const images = useActiveProjectImages();
  const selectedImage = images.find((img) => img.id === selectedImageId);
  const panelRef = useRef<HTMLDivElement>(null);
  scope.current=`${activeProjectId}:${activeBrandId}:${selectedImageId}`;
  useEffect(()=>{scope.current=`${activeProjectId}:${activeBrandId}:${selectedImageId}`;setProductChoice(null);setProductError('');setIsGenerating(false);return()=>{requestEpoch.current++;scope.current='';}},[activeProjectId,activeBrandId,selectedImageId]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!panelRef.current) return;
      // Click inside the panel itself? Stay open.
      if (panelRef.current.contains(target)) return;
      // Click inside the toolbar? Let the toolbar's own button handlers
      // manage state — they explicitly open/close panels via setState.
      // Without this, mousedown on the toolbar's trigger button would call
      // onClose() and then the button's onClick toggles back to open,
      // making the panel feel stuck. It also lets other toolbar buttons
      // (Styles, Aspect Ratio, etc.) take over cleanly.
      const toolbarEl = document.querySelector('[data-testid="canvas-toolbar"]');
      if (toolbarEl && toolbarEl.contains(target)) return;
      // Generation runs fire-and-forget — let the user dismiss the panel
      // anytime; the workflow continues in the background and progress is
      // visible via canvas placeholders + the chat sidebar's playbook card.
      onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  // Auto-fire when something queued an automatic clone (e.g. Ad Library
  // "Clone" action or the canvas-toolbar Clone button). Reads the same store
  // flag as canvas-toolbar.tsx and only fires on the matching image. The
  // count and mode are passed via overrideQty/overrideMode so we don't
  // depend on the qty state-update race.
  useEffect(() => {
    const pending = useCanvasStore.getState().pendingAutoVariations;
    if (!pending) return;
    if (pending.imageId !== selectedImageId) return;
    if (!selectedImage) return; // image hasn't landed on canvas yet — wait
    // Clear the flag first so a re-render or strict-mode double-invoke can't
    // fire it twice.
    useCanvasStore.setState({ pendingAutoVariations: null });
    setQty(pending.count); // also update the UI selector so the panel reflects what we're generating
    if (pending.mode) setActiveMode(pending.mode); // flip header/labels to "Clone" before generation kicks off
    handleGenerate(pending.count, pending.mode, pending.productId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImageId, selectedImage?.id]);

  const handleGenerate = async (overrideQty?: number, overrideMode?: 'clone' | 'variation', overrideProductId?: number) => {
    if (!selectedImage) return;
    const effectiveQty = overrideQty ?? qty;
    const effectiveMode: 'clone' | 'variation' = overrideMode ?? 'variation';
    if(checkingProduct.current||isGenerating)return;
    const requestScope=scope.current;
    const epoch=requestEpoch.current;
    setActiveMode(effectiveMode);
    setIsGenerating(true);
    setProductError('');
    checkingProduct.current=true;
    let resolvedProductId:number;
    try{
      if(!activeBrandId)throw new Error('Choose a brand before generating.');
      const decision=await resolveCanvasProduct({brandId:activeBrandId,projectId:activeProjectId??undefined,productId:overrideProductId,imageUrl:selectedImage.src});
      if((scope.current!==requestScope||requestEpoch.current!==epoch))return;
      if(decision.status==='choice_required'){
        setProductChoice({candidates:decision.candidates,qty:effectiveQty,mode:effectiveMode});
        return;
      }
      resolvedProductId=decision.productId;
      setProductChoice(null);
    }catch(error){if((scope.current===requestScope&&requestEpoch.current===epoch))setProductError(error instanceof Error?error.message:'Could not check products.');return;}
    finally{checkingProduct.current=false;if((scope.current===requestScope&&requestEpoch.current===epoch))setIsGenerating(false);}
    if((scope.current!==requestScope||requestEpoch.current!==epoch))return;
    setIsGenerating(true);
    setCompletedCount(0);

    console.log('[CreateVariationsPanel] generating', effectiveQty, effectiveMode === 'clone' ? 'clones' : `variations at creativity ${creativity}`, 'for:', selectedImage.src.slice(0, 60));

    // Mirror into chat (same pattern as Style Variations). The synthetic
    // messages show the user/assistant exchange in the chat sidebar with
    // thumbnails appended as each variation lands.
    const pinnedProjectId = activeProjectId;
    const userMessageId = `msg-user-createvar-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const assistantMessageId = `msg-ai-createvar-${Date.now() + 1}-${Math.random().toString(36).slice(2)}`;
    const progressGroup = createCloneProgress(assistantMessageId, effectiveQty);
    let sawDone = false;
    const deliveredSlots = new Set<number>();
    const creativityLabel = effectiveMode === 'clone' ? 'Clone' : CREATIVITY_LABEL(creativity);
    const unitNoun = effectiveMode === 'clone' ? 'clone' : 'variation';
    let qaPassCount = 0;
    let completedCountLocal = 0;

    // Pre-compute placeholder positions on a new row below ALL existing
    // canvas content — both real images AND in-flight placeholders. If a
    // prior variation/campaign run is still generating (its placeholders
    // visible but not yet resolved), the new batch must land BELOW those
    // too, not on top of them. Previously this only considered proj.images
    // and ignored proj.canvasPlaceholders, so back-to-back clicks dropped
    // batches at the same y → stacked overlap.
    const GEN_GAP = 24;
    const placeholderDisplayScale = Math.min(320 / (selectedImage.originalWidth || 1080), 320 / (selectedImage.originalHeight || 1080), 1);
    const placeholderDisplayW = Math.round((selectedImage.originalWidth || 1080) * placeholderDisplayScale);
    const placeholderDisplayH = Math.round((selectedImage.originalHeight || 1080) * placeholderDisplayScale);
    // v6.74 — vertical gap source → variation row. The row gets a labeled
    // frame whose top edge sits FRAME_PAD_TOP (52px) ABOVE the tiles; at the
    // old 24px the frame + pill drew ON TOP of the source ad. 90px keeps the
    // frame fully below the source.
    const SOURCE_TO_CLONES_GAP = 90;
    let placeholderTargetY = selectedImage.y + selectedImage.displayHeight + SOURCE_TO_CLONES_GAP;
    if (pinnedProjectId) {
      const proj = useCanvasStore.getState().projects[pinnedProjectId];
      if (proj) {
        const sourceBottom = selectedImage.y + selectedImage.displayHeight;
        const imageBottom = proj.images.reduce(
          (max, im) => Math.max(max, im.y + im.displayHeight),
          sourceBottom,
        );
        const placeholderBottom = Object.values(proj.canvasPlaceholders).reduce(
          (max, ph) => Math.max(max, ph.y + ph.height),
          0,
        );
        const contentBottom = Math.max(imageBottom, placeholderBottom);
        // Two spacing regimes: directly under the source (nothing else below
        // it), the frame just needs to clear the source. Below FOREIGN
        // content, give full run clearance so the frame + pill never touch
        // someone else's row.
        const RUN_CLEARANCE = 110;
        placeholderTargetY = contentBottom > sourceBottom
          ? contentBottom + RUN_CLEARANCE
          : sourceBottom + SOURCE_TO_CLONES_GAP;
      }
    }
    const placeholderTaskIds: string[] = Array.from({ length: effectiveQty }, (_, idx) => `createvar-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`);
    const placeholderPositions: Array<{ x: number; y: number }> = Array.from({ length: effectiveQty }, (_, idx) => ({
      x: selectedImage.x + idx * (placeholderDisplayW + GEN_GAP),
      y: placeholderTargetY,
    }));
    if (pinnedProjectId) {
      const now = Date.now();
      useCanvasStore.getState()._writeMessages(pinnedProjectId, (msgs) => [
        ...msgs,
        {
          id: userMessageId,
          role: 'user' as const,
          content: effectiveMode === 'clone'
            ? `Clone this ad ${effectiveQty} time${effectiveQty === 1 ? '' : 's'}`
            : `Generate ${effectiveQty} variation${effectiveQty === 1 ? '' : 's'} (${creativityLabel})`,
          thumbnails: [selectedImage.src],
          timestamp: now,
        },
        {
          id: assistantMessageId,
          role: 'assistant' as const,
          content: '',
          isStreaming: true,
          isAgentMessage: true,
          agentTaskGroups: [progressGroup],
          thumbnails: [],
          timestamp: now + 1,
        },
      ]);
      // Persist immediately so messages survive reload mid-generation.
      saveConversationHistory(pinnedProjectId).catch(() => {});
    }

    if (pinnedProjectId) useCloneActivity.getState().start(pinnedProjectId, assistantMessageId);
    try {
      const response = await fetch('/api/canvas/create-variations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assistantMessageId,
          imageUrl: selectedImage.src,
          canvasBounds: { left: Math.min(...placeholderPositions.map(p => p.x)), bottom: Math.max(...placeholderPositions.map(p => p.y + placeholderDisplayH)) },
          count: effectiveQty,
          creativity,
          mode: effectiveMode,
          width: selectedImage.originalWidth || 1080,
          height: selectedImage.originalHeight || 1080,
          brandId: activeBrandId ?? undefined,
          projectId: pinnedProjectId ?? undefined,
          // v7.53 — the product the clone picker asked for (multi-product brands).
          productId: resolvedProductId,
        }),
      });

      if (!response.ok || !response.body) {
        const failure = await response.json().catch(() => ({}));
        throw new Error(failure.message || failure.error || 'Clone request failed');
      }

      const reader = response.body.getReader();
      if (pinnedProjectId) setPlaybookStreamActive(pinnedProjectId, assistantMessageId, true);
      const decoder = new TextDecoder();
      let sseBuffer = '';
      // v6.76 — run id streamed by the server; stamped on landed tiles so
      // the batch gets a titled bounding box (frames key on workflowRunId).
      let serverRunId: number | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const blocks = sseBuffer.split('\n\n');
        sseBuffer = blocks.pop() ?? '';

        for (const block of blocks) {
          if (!block.trim()) continue;
          const eventMatch = block.match(/^event: (.+)$/m);
          const dataMatch = block.match(/^data: (.+)$/m);
          if (!eventMatch || !dataMatch) continue;
          const eventType = eventMatch[1].trim();
          let data: Record<string, unknown>;
          try { data = JSON.parse(dataMatch[1]); } catch { continue; }
          if (eventType === 'playbook_queue' && pinnedProjectId) {
            useCanvasStore.getState()._writeMessages(pinnedProjectId, messages => mergePlaybookJobs(messages, [{...data, id: data.jobId ?? data.id, projectId: pinnedProjectId, assistantMessageId} as any], pinnedProjectId));
          }
          if (pinnedProjectId && ['step', 'variation_progress', 'variation_complete', 'done', 'error'].includes(eventType)) {
            useCanvasStore.getState()._writeMessages(pinnedProjectId, msgs => msgs.map(message => message.id === assistantMessageId
              ? {...message, agentTaskGroups: (message.agentTaskGroups ?? []).map(group => applyCloneProgress(group, eventType, data))} : message));
            saveConversationHistory(pinnedProjectId).catch(() => {});
          }
          if (eventType === 'error') throw new Error(typeof data.error === 'string' ? data.error : 'The clone request failed.');


          if (eventType === 'run' && typeof data.runId === 'number') {
            serverRunId = data.runId as number;
            useCanvasStore.getState()._writeCanvasPlaceholders(pinnedProjectId, placeholders => Object.fromEntries(
              Object.entries(placeholders).map(([id, placeholder]) => [id, placeholderTaskIds.includes(id) ? {...placeholder, workflowRunId: serverRunId} : placeholder]),
            ));
          }

          if (eventType === 'variation_complete') {
            if (typeof data.index !== 'number' || !Number.isInteger(data.index) || data.index < 0 || data.index >= effectiveQty || typeof data.imageUrl !== 'string' || !data.imageUrl || deliveredSlots.has(data.index)) continue;
            deliveredSlots.add(data.index);
            const imgUrl = data.imageUrl as string;
            const imgW = (data.width as number) || selectedImage.originalWidth || 1080;
            const imgH = (data.height as number) || selectedImage.originalHeight || 1080;
            const variationIdx = data.index as number;
            const filename = `variation_${variationIdx}_${Date.now()}.jpg`;
            const adMeta = {
              // Set adStyle so AdInfoBadge renders the "i" icon on canvas.
              // The badge condition needs one of: adStyle, adAngle, headline,
              // ctaText, or wavespeedPrompt — without any of these the
              // info icon hides itself.
              adStyle: `Variation (${CREATIVITY_LABEL(creativity)})`,
              conceptName: `Free Variation ${variationIdx + 1} (${CREATIVITY_LABEL(creativity)})`,
              wavespeedPrompt: `Variation of source ad at "${CREATIVITY_LABEL(creativity)}" creativity level`,
              workflowRunId: serverRunId ?? undefined,
            };
            // Resolve placeholder + place at the same coords.
            const taskId = placeholderTaskIds[variationIdx];
            if (taskId && useCanvasStore.getState().projects[pinnedProjectId ?? -1]?.canvasPlaceholders[taskId]) resolveCanvasPlaceholder(taskId, pinnedProjectId ?? undefined);
            const pos = placeholderPositions[variationIdx];
          if (data.serverManaged && pinnedProjectId) {
            void useCanvasStore.getState().reconcileImagesFromServer(pinnedProjectId);
          } else addImageToCanvas(
              imgUrl, imgW, imgH, filename,
              imgW, imgH, `Variation ${variationIdx + 1}`,
              pos?.x, pos?.y, adMeta,
              pinnedProjectId ?? undefined,
            );
            // Mirror progress into the chat.
            completedCountLocal++;
            const thisQa = data.qaPassed as boolean | null | undefined;
            if (thisQa === true) qaPassCount++;
            if (pinnedProjectId) {
              const completedNow = completedCountLocal;
              const passedNow = qaPassCount;
              useCanvasStore.getState()._writeMessages(pinnedProjectId, (msgs) =>
                msgs.map((m) =>
                  m.id === assistantMessageId
                    ? {
                        ...m,
                        thumbnails: [...(m.thumbnails || []), imgUrl],
                        content: completedNow < effectiveQty
                          ? `Generating ${unitNoun} ${completedNow + 1} of ${effectiveQty}…  (QA so far: ${passedNow}/${completedNow})`
                          : `Done — ${effectiveQty} ${unitNoun}${effectiveQty === 1 ? '' : 's'} generated. QA: ${passedNow}/${effectiveQty} passed.`,
                      }
                    : m
                )
              );
            }
            // Drop the QA badge into the project's imageQaBadges map and
            // PATCH the canvas_image row once its client_id is known.
            const qaPassed = data.qaPassed as boolean | null | undefined;
            const qaReason = data.qaReason as string | null | undefined;
            if (activeProjectId && (qaPassed !== undefined && qaPassed !== null)) {
              const passed = !!qaPassed;
              const reasonStr = qaReason || (passed ? 'QA Passed' : 'QA Failed');
              useCanvasStore.getState()._writeQaBadges(activeProjectId, (badges) => ({
                ...badges,
                [imgUrl]: { passed, score: 0, badgeType: passed ? 'pass' : 'fail', reason: reasonStr, attempt: 0 },
              }));
              (async () => {
                for (let attempt = 0; attempt < 12; attempt++) {
                  await new Promise((r) => setTimeout(r, 250));
                  const projState = useCanvasStore.getState().projects[activeProjectId];
                  const found = projState?.images?.find((im) => im.src === imgUrl);
                  if (found?.id) {
                    try {
                      await fetch(`/api/canvas/projects/${activeProjectId}/images/${found.id}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ qaPassed: passed, qaReason: reasonStr }),
                      });
                    } catch (e) {
                      console.warn('[CreateVariationsPanel] qa PATCH failed:', e);
                    }
                    break;
                  }
                }
              })();
            }
            setCompletedCount((c) => c + 1);
          }

          if (eventType === 'done') {
            sawDone = true;
            placeholderTaskIds.forEach((id, index) => { if (!deliveredSlots.has(index) && useCanvasStore.getState().projects[pinnedProjectId ?? -1]?.canvasPlaceholders[id]) failCanvasPlaceholder(id, 'This clone could not finish.', pinnedProjectId ?? undefined); });
            // Stop streaming + persist final state (all thumbnails) so the
            // variation history survives reload.
            if (pinnedProjectId) {
              useCanvasStore.getState()._writeMessages(pinnedProjectId, (msgs) =>
                msgs.map((m) => m.id === assistantMessageId ? { ...m, isStreaming: false, content: `${deliveredSlots.size} of ${effectiveQty} ${unitNoun}${effectiveQty === 1 ? '' : 's'} delivered.${deliveredSlots.size < effectiveQty ? ' Some could not finish; retry the missing ones.' : ''}` } : m)
              );
              saveConversationHistory(pinnedProjectId).catch(() => {});
            }
            // Auto-fit canvas to show source + variations.
            if (activeProjectId && selectedImage) {
              const proj = useCanvasStore.getState().projects[activeProjectId];
              if (proj) {
                const sourceY = selectedImage.y;
                const sourceX = selectedImage.x;
                const rowImages = proj.images.filter((im) =>
                  im.y >= sourceY && im.y <= sourceY + selectedImage.displayHeight + 40 &&
                  im.x >= sourceX - 80
                );
                if (rowImages.length > 0) {
                  const minX = Math.min(...rowImages.map((im) => im.x));
                  const maxX = Math.max(...rowImages.map((im) => im.x + im.displayWidth));
                  const minY = Math.min(...rowImages.map((im) => im.y));
                  const maxY = Math.max(...rowImages.map((im) => im.y + im.displayHeight));
                  useCanvasStore.getState()._writeProjectView(activeProjectId, {
                    pendingFitToRow: { minX, minY, maxX, maxY },
                    isCanvasOpen: true,
                  });
                }
              }
            }
            break;
          }
        }
      }
      if (!sawDone) throw new Error('The clone connection ended before the run finished.');
    } catch (err) {
      console.error('[CreateVariationsPanel] Error:', err);
      placeholderTaskIds.forEach((tid, index) => { if (!deliveredSlots.has(index) && useCanvasStore.getState().projects[pinnedProjectId ?? -1]?.canvasPlaceholders[tid]) failCanvasPlaceholder(tid, 'Generation interrupted', pinnedProjectId ?? undefined); });
      if (pinnedProjectId) useCanvasStore.getState()._writeMessages(pinnedProjectId, msgs => msgs.map(message => message.id === assistantMessageId
        ? {...message, isStreaming: false, content: `Cloning stopped: ${err instanceof Error ? err.message : 'The request failed.'}`, agentTaskGroups: (message.agentTaskGroups ?? []).map(group => applyCloneProgress(group, 'error', {}))} : message));
    } finally {
      if (pinnedProjectId) setPlaybookStreamActive(pinnedProjectId, assistantMessageId, false);
      if (pinnedProjectId) {
        useCloneActivity.getState().finish(pinnedProjectId, assistantMessageId);
        saveConversationHistory(pinnedProjectId).catch(() => {});
      }
      setIsGenerating(false);
    }
  };

  return (
    <div
      ref={panelRef}
      data-testid="create-variations-panel"
      style={{
        position: 'absolute',
        bottom: 'calc(100% + 12px)',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 360,
        background: '#FFFFFF',
        border: '1px solid #E8E5E0',
        borderRadius: 14,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        zIndex: 300,
        overflow: 'hidden',
      }}
    >
      {productError&&<p role="alert" style={{padding:16,color:'#b91c1c'}}>{productError}</p>}
      {productChoice&&<div data-testid="variation-product-choice" style={{padding:16}}><p>Which product should these ads feature?</p>{productChoice.candidates.map(product=><button type="button" key={product.id} disabled={isGenerating} onClick={()=>void handleGenerate(productChoice.qty,productChoice.mode,product.id)} style={{display:'block',padding:8,marginTop:6}}>{product.name}</button>)}</div>}
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 12px' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1A1A1A' }}>
            {isCloneMode ? 'Clone Ad' : 'Create Variations'}
          </div>
          <div style={{ fontSize: 11, color: '#8C8578', marginTop: 2 }}>
            {isCloneMode
              ? "Same layout + style, swapped to this brand's product"
              : 'AI will analyze your ad and create variations'}
          </div>
        </div>
        <button
          data-testid="create-variations-close"
          onClick={onClose}
          disabled={isGenerating}
          style={{ background: 'none', border: 'none', cursor: isGenerating ? 'not-allowed' : 'pointer', color: '#8C8578', padding: 4, borderRadius: 6 }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Qty selector */}
      <div style={{ padding: '10px 16px', borderTop: '1px solid #F0EDE8', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, color: '#8C8578', marginRight: 4 }}>Qty:</span>
        {[1, 2, 3, 4].map((n) => (
          <button
            key={n}
            data-testid={`variations-qty-${n}`}
            onClick={() => !isGenerating && setQty(n)}
            disabled={isGenerating}
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              border: 'none',
              background: qty === n ? '#6366F1' : 'rgba(255,255,255,0.07)',
              color: qty === n ? '#FFFFFF' : '#9CA3AF',
              fontWeight: 600,
              fontSize: 14,
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {n}
          </button>
        ))}
      </div>

      {/* Creativity slider — only shown in variation mode. Clone mode is a
          strict reproduction so the slider doesn't apply. */}
      {!isCloneMode && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid #F0EDE8' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: '#8C8578' }}>Creativity:</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#6366F1' }}>{CREATIVITY_LABEL(creativity)}</span>
          </div>
          <input
            data-testid="creativity-slider"
            type="range"
            min={0}
            max={100}
            value={creativity}
            onChange={(e) => setCreativity(Number(e.target.value))}
            disabled={isGenerating}
            style={{ width: '100%', accentColor: '#6366F1' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <span style={{ fontSize: 10, color: '#B0ACA6' }}>Copy changes</span>
            <span style={{ fontSize: 10, color: '#B0ACA6' }}>Art + colors</span>
          </div>
        </div>
      )}

      {/* Progress */}
      {isGenerating && (
        <div style={{ padding: '10px 16px', borderTop: '1px solid #F0EDE8', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Loader2 size={14} style={{ color: '#6366F1', animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: 12, color: '#6366F1' }}>
            Generating {completedCount} / {qty} {isCloneMode ? 'clones' : 'variations'}…
          </span>
        </div>
      )}

      {/* Generate button */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #F0EDE8' }}>
        <button
          data-testid="variations-generate-btn"
          onClick={() => handleGenerate(productChoice?.qty, productChoice?.mode ?? activeMode)}
          disabled={isGenerating || !selectedImage}
          style={{
            width: '100%',
            padding: '11px 0',
            borderRadius: 9,
            background: isGenerating || !selectedImage ? '#E8E5E0' : '#6366F1',
            color: '#FFFFFF',
            fontWeight: 600,
            fontSize: 14,
            border: 'none',
            cursor: isGenerating || !selectedImage ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            opacity: !selectedImage ? 0.5 : 1,
          }}
        >
          {isGenerating
            ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</>
            : isCloneMode
              ? `Clone ${qty} Time${qty !== 1 ? 's' : ''}`
              : `Generate ${qty} Variation${qty !== 1 ? 's' : ''}`
          }
        </button>
      </div>
    </div>
  );
}
